<script setup lang="ts">
import { initFlowbite } from 'flowbite';
import { useStoryblokBridge, type StoryblokComponentType } from '@storyblok/vue';
import { useStoryblokVersion } from '~/composables/use-storyblok-version';
import { ensurePinterestScriptLoaded } from '~/utils/pinterest';

const { params } = useRoute();
const { slug } = params;
const { locale } = useI18n();
const { language } = useMarket();

const url = slug && Array.isArray(slug) && slug.length > 0 ? slug.join('/') : 'home';

// Use language (en, sv, de, pt) for Storyblok, not locale (en-gb, sv-se, etc.)
const story = await useAsyncStoryblok(url.replace(/\/$/, ''), {
    version: useStoryblokVersion(),
    language: language.value,
});

if (story.value.status) {
    throw createError({
        statusCode: story.value.status,
        statusMessage: story.value.response,
    });
}

const { meta } = await useDefaultMetatags(story, locale);

const isPinterestArticle = computed(() => {
    const component = story.value?.content?.component;
    return component === 'article' || component === 'article-md';
});

const initialisePinterest = async () => {
    if (!import.meta.client || !isPinterestArticle.value) {
        return;
    }

    try {
        await ensurePinterestScriptLoaded({
            hover: true,
            save: true,
        });
        await nextTick();
        window.PinUtils?.build?.();
    } catch (error) {
        console.warn('Failed to initialise Pinterest save buttons', error);
    }
};

if (import.meta.client) {
    watch(
        () => isPinterestArticle.value,
        (shouldEnable) => {
            if (shouldEnable) {
                initialisePinterest();
            }
        },
        { immediate: true },
    );

    watch(
        () => story.value?.content,
        () => {
            if (isPinterestArticle.value) {
                initialisePinterest();
            }
        },
        { immediate: true },
    );
}

onMounted(() => {
    initFlowbite();
    if (story.value && story.value.id) {
        useStoryblokBridge(story.value.id, (evStory) => (story.value = evStory));
    }

    initialisePinterest();
});

provide('story', story);

useServerSeoMeta(meta);

const { locales: allLocales, localeToMarket } = useAppConfig();

const route = useRoute();
const currentPath = computed(() => {
    // Remove locale prefix if present (e.g., /en-gb/home -> home)
    const path = route.path;
    for (const loc of allLocales) {
        if (path.startsWith(`/${loc}/`)) {
            return path.substring(loc.length + 2);
        }
        if (path === `/${loc}`) {
            return '';
        }
    }
    return path.startsWith('/') ? path.substring(1) : path;
});

// Generate hreflang links for the new locale-based URL structure
const generateHreflangLinks = () => {
    const links: Array<{ rel: string; href: string; hreflang: string }> = [];
    const baseUrl = 'https://tiptapp.com';

    // Special case: map root path to "home"
    const path = currentPath.value === '' ? 'home' : currentPath.value;

    // Check if visibleIn property exists and has values
    const visibleIn: string[] = story.value?.content?.visibleIn || [];
    const hasVisibilityRestrictions = Array.isArray(visibleIn) && visibleIn.length > 0;

    // Helper to check if a locale should be included based on its market
    const shouldIncludeLocale = (loc: Locale): boolean => {
        if (!hasVisibilityRestrictions) return true;
        const market = localeToMarket[loc];
        // visibleIn uses market codes (uk, se, de, pt, at)
        return visibleIn.includes(market);
    };

    // Locale to hreflang mapping (locale code -> hreflang value)
    const localeToHreflang: Record<Locale, string> = {
        'en-gb': 'en-GB',
        'en-se': 'en-SE',
        'en-de': 'en-DE',
        'en-at': 'en-AT',
        'en-pt': 'en-PT',
        'sv-se': 'sv-SE',
        'de-de': 'de-DE',
        'de-at': 'de-AT',
        'pt-pt': 'pt-PT',
    };

    // Add x-default pointing to en-gb (primary English)
    // Include trailing slash to match Netlify's canonical URL format
    if (shouldIncludeLocale('en-gb')) {
        links.push({
            rel: 'alternate',
            href: `${baseUrl}/en-gb/${path}/`,
            hreflang: 'x-default',
        });
    }

    // Add hreflang for each locale
    for (const loc of allLocales) {
        if (shouldIncludeLocale(loc as Locale)) {
            links.push({
                rel: 'alternate',
                href: `${baseUrl}/${loc}/${path}/`,
                hreflang: localeToHreflang[loc as Locale],
            });
        }
    }

    return links;
};

const hreflangLinks = computed(() => generateHreflangLinks());

const canonicalUrl = computed(() => {
    const baseUrl = 'https://tiptapp.com';
    // Add trailing slash to match Netlify's URL format (avoids 301 redirect)
    const pathWithSlash = route.path.endsWith('/') ? route.path : `${route.path}/`;
    return `${baseUrl}${pathWithSlash}`;
});

useHead({
    link: [
        { rel: 'canonical', href: canonicalUrl.value },
        ...hreflangLinks.value,
    ],
});
</script>

<template>
    <StoryblokComponent
        v-if="story"
        :blok="{
            ...story.content,
            published_at: story.published_at,
            created_at: story.created_at,
            first_published_at: story.first_published_at,
        }"
    />
</template>
